import { invoke } from '@forge/bridge'
import { proxy } from 'valtio'
import type { UserSettings } from '../../../src/schema/zodSchemas'
import type {
  DeleteRadarResult,
  Env,
  ItemRelease,
  LicenseCheckResponse,
  RadarConfig,
  RadarData,
  RadarDataStorage,
  RadarMetadata
} from '../../../types'
import { state } from './state'

export interface ExportedConfig {
  config: RadarConfig
  schemaVersion: number
}

const updateAvailableRadars = (radars: RadarMetadata[]) => {
  state.availableRadars = proxy([...radars])
}

export const api = {
  // Write operations - not available in embedded mode
  updateItemProperties: async (
    itemId: string,
    quadrant: string,
    tags: string[],
    archived: boolean,
    radarId?: number
  ): Promise<RadarData> =>
    await invoke<RadarData>('updateItemProperties', {
      itemId,
      quadrant,
      tags,
      archived,
      radarId
    }),

  addRelease: async (
    itemId: string,
    release: ItemRelease,
    radarId?: number
  ): Promise<RadarData> =>
    await invoke<RadarData>('addRelease', { itemId, release, radarId }),

  deleteRelease: async (
    itemId: string,
    releaseId: string,
    radarId?: number
  ): Promise<RadarData> =>
    await invoke<RadarData>('deleteRelease', {
      itemId,
      releaseId,
      radarId
    }),

  deleteItem: async (itemId: string, radarId?: number): Promise<RadarData> =>
    await invoke<RadarData>('deleteItem', { itemId, radarId }),

  updateRelease: async (
    itemId: string,
    release: ItemRelease,
    radarId?: number
  ): Promise<RadarData> =>
    await invoke<RadarData>('updateRelease', {
      itemId,
      release,
      radarId
    }),

  setConfig: async (
    config: Partial<RadarConfig> & { version: string },
    radarId?: number
  ): Promise<RadarConfig> =>
    await invoke<RadarConfig>('setConfig', { config, radarId }),

  getEnv: async (): Promise<Env & { userSettings?: UserSettings }> =>
    await invoke('getEnv'),

  getVersion: async (): Promise<string> => await invoke<string>('getVersion'),

  // Read operations
  getConfig: async (radarId?: number): Promise<RadarConfig> =>
    await invoke('getConfig', { radarId }),

  getRadarData: async (radarId?: number): Promise<RadarData> =>
    await invoke('getRadarData', { radarId }),

  setRadarData: async (
    radarData: RadarData,
    radarId: number
  ): Promise<RadarData> => await invoke('setRadarData', { radarData, radarId }),

  exportConfig: async (radarId?: number): Promise<ExportedConfig> =>
    await invoke('exportRadarConfig', { radarId }),

  importConfig: async (
    config: RadarConfig,
    radarId?: string
  ): Promise<RadarConfig> =>
    await invoke('importRadarConfig', { config, radarId }),

  getRadarsConfig: async (): Promise<RadarMetadata[]> => {
    const response = await invoke<{ radars: RadarMetadata[] }>(
      'getRadarsConfig'
    )
    updateAvailableRadars(response.radars)
    return response.radars || []
  },

  // Write operations
  createRadar: async (name: string): Promise<RadarMetadata[]> => {
    const response: RadarMetadata[] = await invoke('createRadar', { name })
    updateAvailableRadars(response)
    return response
  },

  deleteRadar: async (radarId: number): Promise<DeleteRadarResult> =>
    await invoke('deleteRadar', { radarId }),

  updateRadar: async (radarId: number, name: string): Promise<RadarMetadata> =>
    await invoke('updateRadar', { radarId, name }),

  deleteAllItemsForRadar: async (radarId: number): Promise<boolean> =>
    await invoke('deleteAllItemsForRadar', { radarId }),

  checkLicenseLimit: async (radarId?: number): Promise<LicenseCheckResponse> =>
    await invoke<LicenseCheckResponse>('checkLicenseLimit', { radarId }),

  checkRadarLimit: async (): Promise<{
    canCreateRadar: boolean
    currentCount: number
    maxAllowed: number
  }> =>
    await invoke<{
      canCreateRadar: boolean
      currentCount: number
      maxAllowed: number
    }>('checkRadarLimit', {}),

  // Billing & Subscription API - disabled in embedded mode
  checkSubscriptionStatus: async (): Promise<
    import('../../../types').LicenseStatus
  > =>
    await invoke<import('../../../types').LicenseStatus>(
      'checkSubscriptionStatus'
    ),

  createCheckoutSession: async (
    tier: import('../../../types').SubscriptionTier,
    interval: import('../../../types').BillingInterval
  ): Promise<{ url: string; sessionId: string }> =>
    await invoke<{ url: string; sessionId: string }>('createCheckoutSession', {
      tier,
      interval
    }),

  createCustomerPortalSession: async (): Promise<{ url: string }> =>
    await invoke<{ url: string }>('createCustomerPortalSession'),

  /**
   * Export all radars with metadata and data (full backup)
   */
  exportAllRadars: async (): Promise<{
    schemaVersion: number
    radars: RadarMetadata[]
    data: Record<number, RadarDataStorage>
  }> => await invoke('exportAllRadars', {}),

  // --- Webhook URL APIs ---
  setWebhookSecret: async (secret: string) =>
    await invoke('setWebhookSecret', { secret }),
  getWebhookSecret: async () => {
    const result = await invoke<{ secret: string }>('getWebhookSecret')
    return result.secret
  },
  getWebhookUrl: async () => await invoke('getWebhookUrl'),
  rotateWebhook: async () => await invoke('rotateWebhook'),

  // --- User Settings APIs ---
  updateUserSettings: async (
    updates: Partial<UserSettings>
  ): Promise<UserSettings> =>
    await invoke<UserSettings>('updateUserSettings', { updates }),

  submitEmail: async (email: string): Promise<{ success: boolean }> =>
    await invoke<{ success: boolean }>('submitEmail', { email }),

  clearEmail: async (): Promise<{ success: boolean }> =>
    await invoke<{ success: boolean }>('submitEmail', { email: '' })
}
